import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

// -----------------------------------------------------------------------------
// Badge.
//
// 4px corners, not a pill - badges here sit inside dense table cells and a pill
// reads as a button. Each status variant pairs a `soft` background with its own
// full-strength foreground, so the text clears AA against the chip it sits on.
// -----------------------------------------------------------------------------

const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-xs font-medium leading-4 whitespace-nowrap',
  {
    variants: {
      variant: {
        neutral: 'border-border bg-muted text-secondary-foreground',
        brand: 'border-lavender bg-lavender-tint text-primary',
        success: 'border-success/25 bg-success-soft text-success',
        warning: 'border-warning/30 bg-warning-soft text-[#92400E]',
        danger: 'border-destructive/25 bg-danger-soft text-destructive',
        info: 'border-info/25 bg-info-soft text-info',
        outline: 'border-input bg-transparent text-secondary-foreground',
      },
    },
    defaultVariants: {
      variant: 'neutral',
    },
  },
);

export type BadgeProps = React.HTMLAttributes<HTMLSpanElement> &
  VariantProps<typeof badgeVariants>;

function Badge({ className, variant, ...props }: BadgeProps): JSX.Element {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge, badgeVariants };
